import { type ReactNode, useRef } from 'react';

import { MenuItem } from '../menu';

import { useDropdown } from './context';

export interface DropdownMenuItemProps {
  /** Item ID - passed to onSelect */
  id: string;
  /** Item content */
  children: ReactNode;
  /** Content to display on the right side */
  rightContent?: ReactNode;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Whether this is a destructive action (displays in red) */
  destructive?: boolean;
  /** Additional className for outer container */
  className?: string;
  /** Additional className for inner container */
  innerClassName?: string;
}

/**
 * DropdownMenuItem - individual menu item for use within Dropdown
 *
 * Uses MenuItem internally with dropdown context integration.
 *
 * @example
 * ```tsx
 * <DropdownMenuItem id="edit">Edit</DropdownMenuItem>
 *
 * // Disabled
 * <DropdownMenuItem id="archive" disabled>Archive</DropdownMenuItem>
 *
 * // Destructive
 * <DropdownMenuItem id="delete" destructive>Delete</DropdownMenuItem>
 *
 * // With right content
 * <DropdownMenuItem id="shortcut" rightContent={<span className="text-gray-400">⌘K</span>}>
 *   Search
 * </DropdownMenuItem>
 * ```
 */
export function DropdownMenuItem({
  id,
  children,
  rightContent,
  disabled = false,
  destructive = false,
  className = '',
  innerClassName = '',
}: DropdownMenuItemProps) {
  const { size, activeIndex, onItemSelect, getItemProps, listRef, registerItem } = useDropdown();
  const indexRef = useRef<number>(-1);
  const itemRef = useRef<HTMLButtonElement>(null);

  // Register this item and get its index (only once on mount)
  if (indexRef.current === -1) {
    indexRef.current = registerItem();
  }

  const isActive = activeIndex === indexRef.current;

  return (
    <MenuItem
      ref={(node) => {
        itemRef.current = node;
        if (indexRef.current >= 0) {
          listRef.current[indexRef.current] = node;
        }
      }}
      size={size}
      disabled={disabled}
      destructive={destructive}
      isActive={isActive}
      rightContent={rightContent}
      className={className}
      innerClassName={innerClassName}
      tabIndex={isActive ? 0 : -1}
      {...getItemProps({
        onClick: () => !disabled && onItemSelect(id),
        onKeyDown: (e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            onItemSelect(id);
          }
        },
      })}
    >
      {children}
    </MenuItem>
  );
}
